'use client';
import React, { useState } from 'react';
import { Globe, X, MapPin, ArrowRight, Bell } from 'lucide-react';

const ZIMBABWE_COUNTRIES = ['ZW'];

const AFRICAN_COUNTRIES: Record<string, string> = {
  DZ: 'Algeria', AO: 'Angola', BJ: 'Benin', BW: 'Botswana', BF: 'Burkina Faso',
  BI: 'Burundi', CV: 'Cape Verde', CM: 'Cameroon', CF: 'Central African Republic',
  TD: 'Chad', KM: 'Comoros', CG: 'Congo', CD: 'DR Congo', CI: "Côte d'Ivoire",
  DJ: 'Djibouti', EG: 'Egypt', GQ: 'Equatorial Guinea', ER: 'Eritrea', SZ: 'Eswatini',
  ET: 'Ethiopia', GA: 'Gabon', GM: 'Gambia', GH: 'Ghana', GN: 'Guinea',
  GW: 'Guinea-Bissau', KE: 'Kenya', LS: 'Lesotho', LR: 'Liberia', LY: 'Libya',
  MG: 'Madagascar', MW: 'Malawi', ML: 'Mali', MR: 'Mauritania', MU: 'Mauritius',
  MA: 'Morocco', MZ: 'Mozambique', NA: 'Namibia', NE: 'Niger', NG: 'Nigeria',
  RW: 'Rwanda', ST: 'São Tomé and Príncipe', SN: 'Senegal', SC: 'Seychelles',
  SL: 'Sierra Leone', SO: 'Somalia', ZA: 'South Africa', SS: 'South Sudan',
  SD: 'Sudan', TZ: 'Tanzania', TG: 'Togo', TN: 'Tunisia', UG: 'Uganda',
  ZM: 'Zambia',
};

interface AfricaComingSoonProps {
  countryCode?: string | null;
}

export default function AfricaComingSoon({ countryCode }: AfricaComingSoonProps) {
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (dismissed) return null;
  if (!countryCode) return null;
  if (ZIMBABWE_COUNTRIES.includes(countryCode)) return null;
  if (!AFRICAN_COUNTRIES[countryCode]) return null;

  const countryName = AFRICAN_COUNTRIES[countryCode];

  const handleNotify = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubmitted(true);
    }
  };

  return (
    <div
      className="relative overflow-hidden border-b"
      style={{
        background: 'linear-gradient(135deg, #2C5E3B0D 0%, #E05A470A 50%, #F39C120A 100%)',
        borderColor: '#2C5E3B33',
      }}
    >
      {/* Decorative background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232C5E3B' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-4 relative">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mt-0.5"
            style={{ backgroundColor: '#2C5E3B18' }}
          >
            <Globe size={20} style={{ color: '#2C5E3B' }} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: '#2C5E3B18', color: '#2C5E3B' }}
                  >
                    Coming Soon
                  </span>
                  <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <MapPin size={11} />
                    <span>{countryName}</span>
                  </div>
                </div>

                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
                  Griott is growing across Africa — {countryName} is next.
                </p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)', maxWidth: 600 }}>
                  Our archive is currently focused on Zimbabwean history, but we're building toward a pan-African platform.
                  If you have stories, oral traditions, or historical knowledge from {countryName}, we want to hear from you.
                </p>

                {!submitted ? (
                  <form onSubmit={handleNotify} className="flex items-center gap-2 mt-3 flex-wrap">
                    <div
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full flex-1 min-w-0"
                      style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', maxWidth: 320 }}
                    >
                      <Bell size={13} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
                      <input
                        type="email"
                        placeholder="Get notified when we launch in your region"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="flex-1 bg-transparent text-xs outline-none min-w-0"
                        style={{ color: 'var(--foreground)' }}
                      />
                    </div>
                    <button
                      type="submit"
                      className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all active:scale-95 whitespace-nowrap"
                      style={{ backgroundColor: '#2C5E3B', color: '#fff' }}
                    >
                      Notify me
                      <ArrowRight size={12} />
                    </button>
                    <a
                      href="/contribution-submission"
                      className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all active:scale-95 whitespace-nowrap border"
                      style={{ borderColor: '#2C5E3B44', color: '#2C5E3B', backgroundColor: 'transparent' }}
                    >
                      Contribute anyway
                    </a>
                  </form>
                ) : (
                  <div className="flex items-center gap-2 mt-3">
                    <div
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: '#2C5E3B18', color: '#2C5E3B' }}
                    >
                      ✓ You're on the list! We'll notify you when {countryName} launches.
                    </div>
                  </div>
                )}
              </div>

              {/* Dismiss */}
              <button
                onClick={() => setDismissed(true)}
                className="flex-shrink-0 p-1.5 rounded-full transition-colors mt-0.5"
                style={{ color: 'var(--muted-foreground)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                aria-label="Dismiss"
              >
                <X size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
